import { Camera, ImageOff, Loader2, Trash2, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FileDropzone } from '@/components/ui/file-dropzone';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeletePartPhoto,
  useUploadPartPhoto,
  usePartPhotoUrl,
} from '@/features/part-storage/queries';
import {
  ALLOWED_PHOTO_TYPES,
  PHOTO_MAX_BYTES,
} from '@/features/part-storage/types';
import { ApiError } from '@/lib/api';

interface PartPhotoCardProps {
  partId: string;
  photoKey: string | null;
}

/**
 * Twin of VesselPhotoCard from L1.3, adapted to parts. The L2 spec
 * explicitly says "calque, pas de generic — défer EntityPhotoCard<T>
 * to a polish lot". Two concrete cards stay easier to evolve while
 * the shape settles.
 */
export function PartPhotoCard({ partId, photoKey }: PartPhotoCardProps) {
  const { t } = useTranslation();
  const { data: photoUrl, isLoading: isUrlLoading } = usePartPhotoUrl(
    photoKey ? partId : undefined,
  );
  const uploadMut = useUploadPartPhoto(partId);
  const deleteMut = useDeletePartPhoto(partId);

  const [progress, setProgress] = useState<number | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorParams, setErrorParams] = useState<Record<string, unknown>>();
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  useEffect(() => {
    if (uploadMut.isPending || deleteMut.isPending) {
      setErrorKey(null);
      setErrorParams(undefined);
    }
  }, [uploadMut.isPending, deleteMut.isPending]);

  const onFiles = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setProgress(0);
    uploadMut.mutate(
      {
        file,
        onProgress: (loaded, total) =>
          setProgress(total > 0 ? Math.round((loaded / total) * 100) : 0),
      },
      {
        onSettled: () => setProgress(null),
        onError: (err) => {
          if (err instanceof ApiError) {
            setErrorKey(err.key);
            setErrorParams(err.params);
          } else {
            setErrorKey('part.photo.uploadError');
          }
        },
      },
    );
  };

  const onRemove = () => {
    deleteMut.mutate(undefined, {
      onSuccess: () => setConfirmRemoveOpen(false),
      onError: (err) => {
        setConfirmRemoveOpen(false);
        if (err instanceof ApiError) {
          setErrorKey(err.key);
          setErrorParams(err.params);
        } else {
          setErrorKey('part.photo.removeError');
        }
      },
    });
  };

  const isBusy = uploadMut.isPending || deleteMut.isPending;
  const hasPhoto = Boolean(photoKey);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Camera aria-hidden className="h-4 w-4" />
            {t('part.photo.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorKey && (
            <Alert variant="destructive">
              <AlertTitle>{t('part.photo.uploadError')}</AlertTitle>
              <AlertDescription>
                {t(errorKey, {
                  defaultValue: t('part.photo.uploadError'),
                  ...(errorParams ?? {}),
                })}
              </AlertDescription>
            </Alert>
          )}

          {hasPhoto ? (
            <div className="space-y-3">
              {isUrlLoading || !photoUrl ? (
                <Skeleton className="aspect-square w-full" />
              ) : (
                <img
                  src={photoUrl.url}
                  alt={t('part.photo.title')}
                  className="aspect-square w-full rounded-md object-cover"
                />
              )}
              <div className="flex flex-wrap gap-2">
                <FileDropzone
                  accept={ALLOWED_PHOTO_TYPES.join(',')}
                  maxSizeBytes={PHOTO_MAX_BYTES}
                  maxFiles={1}
                  disabled={isBusy}
                  label={
                    progress !== null
                      ? t('part.photo.uploading')
                      : t('part.photo.replace')
                  }
                  onFilesSelected={onFiles}
                  onError={(e) => {
                    setErrorKey(e.key);
                    setErrorParams(e.params);
                  }}
                  className="flex-1 py-3"
                />
                <Button
                  variant="destructive"
                  onClick={() => setConfirmRemoveOpen(true)}
                  disabled={isBusy}
                >
                  {deleteMut.isPending ? (
                    <Loader2 className="animate-spin" aria-hidden />
                  ) : (
                    <Trash2 aria-hidden />
                  )}
                  {t('part.photo.remove')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex aspect-square w-full items-center justify-center rounded-md border border-dashed bg-muted/30 text-muted-foreground">
                <div className="flex flex-col items-center gap-1 text-sm">
                  <ImageOff className="h-6 w-6" aria-hidden />
                  <span>{t('part.photo.empty')}</span>
                </div>
              </div>
              <FileDropzone
                accept={ALLOWED_PHOTO_TYPES.join(',')}
                maxSizeBytes={PHOTO_MAX_BYTES}
                maxFiles={1}
                disabled={isBusy}
                label={
                  progress !== null ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      {t('part.photo.uploading')}
                      {progress > 0 ? ` ${progress}%` : null}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Upload className="h-4 w-4" aria-hidden />
                      {t('part.photo.upload')}
                    </span>
                  )
                }
                onFilesSelected={onFiles}
                onError={(e) => {
                  setErrorKey(e.key);
                  setErrorParams(e.params);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmRemoveOpen}
        onOpenChange={setConfirmRemoveOpen}
        title={t('part.photo.removeConfirmTitle')}
        description={t('part.photo.removeConfirmDescription')}
        confirmLabel={t('part.photo.removeConfirm')}
        variant="destructive"
        loading={deleteMut.isPending}
        onConfirm={onRemove}
      />
    </>
  );
}
